import { YES_NO, STATUS_VACANT_PRE_RENTED } from "../constants"
import {
  furnishingOptions,
  constructionStatusOptions,
  occupancyStatusOptions,
  tenancyTypeOptions,
  approvalsInPlaceIndustrialOptions,
  buildingPlotAreaUnitOptions,
} from "../sharedOptions"
import type { CategorySection } from "../types"

export const warehouseSections: Record<string, CategorySection[]> = {
  "Warehouse (Pre-Rented)": [
    {
      sectionTitle: "Plot & Building Specifications",
      fields: [
        {
          key: "plotAreaUnit",
          type: "dropdown",
          label: "Plot Area Unit",
          options: buildingPlotAreaUnitOptions,
          placeholder: "Unit",
          tooltip: "Default Sq. Ft. Area is converted to sq. ft. for reports (plotAreaSqFt).",
        },
        { key: "plotArea", type: "number", label: "Plot Area", placeholder: "E.g. 5000" },
        { key: "superAreaSqFt", type: "number", label: "Super Area (sq. ft.)", placeholder: "" },
        { key: "clearHeight", type: "text", label: "Clear Height", placeholder: "" },
        { key: "numberOfLoadingDocks", type: "number", label: "Number of Loading Docks", placeholder: "" },
        { key: "floorLoadCapacityKgSqM", type: "number", label: "Floor Load Capacity (kg/sq. m.)", placeholder: "" },
        { key: "powerLoadKva", type: "number", label: "Power Load (KVA)", placeholder: "" },
        { key: "truckAccessibility", type: "text", label: "Truck Accessibility", placeholder: "" },
        { key: "isCarParkingIncluded", type: "yesno", label: "Is Car Parking Included?", options: YES_NO },
        { key: "totalNumberCarParks", type: "number", label: "Total Number of Car Parks", placeholder: "", showWhen: { key: "isCarParkingIncluded", value: "Yes" } },
      ],
    },
    {
      sectionTitle: "Tenancy Details",
      fields: [
        { key: "occupancyStatus", type: "dropdown", label: "Occupancy Status", options: occupancyStatusOptions, placeholder: "Leased / Pre-Rented", tooltip: "Defaulted to Leased / Pre-Rented." },
        { key: "tenancyType", type: "dropdown", label: "Tenancy Type", options: tenancyTypeOptions, placeholder: "Select", tooltip: "Is the building occupied by a Single Tenant or Multi-tenanted?" },
        { key: "tenantName", type: "text", label: "Tenant Name", placeholder: "", showWhen: { key: "tenancyType", value: "Single Tenant" }, tooltip: "Enter the full legal name of the tenant company." },
        { key: "tenantBusiness", type: "textarea", label: "Tenant Business", placeholder: "", showWhen: { key: "tenancyType", value: "Single Tenant" }, tooltip: "Briefly describe the tenant's industry. E.g., 3PL Logistics, E-commerce Fulfillment." },
        { key: "rentRollMultiTenanted", type: "file", label: "Rent Roll (if multi-tenanted)", fullWidth: true, showWhen: { key: "tenancyType", value: "Multi-tenanted" }, tooltip: "Crucial Document: Upload the detailed rent roll (PDF/Excel) showing all tenants, their leased areas, rent, and lease expiry dates." },
      ],
    },
    {
      sectionTitle: "Investment Details",
      fields: [
        { key: "currentGrossMonthlyRentalAllTenants", type: "number", label: "Current Gross Monthly Rental (All Tenants)", placeholder: "e.g. 500000", tooltip: "Enter the total gross monthly rent from all tenants." },
        { key: "desiredRentalYieldRoiPercent", type: "number", label: "Desired Rental Yield / ROI (%)", placeholder: "e.g. 8", tooltip: "Enter the target rental yield you want to offer the investor." },
        { key: "totalSalePrice", type: "number", label: "Total Sale Price", placeholder: "e.g. 75000000", tooltip: "Enter or system will calculate based on ROI. Formula: (Annual Rent / ROI)." },
        { key: "pricePerSqFt", type: "number", label: "Price Per Sq. Ft. [Auto-Calculated]", placeholder: "e.g. 12000", tooltip: "System will calculate this. Formula: (Total Price / Total Built-up Area)." },
      ],
    },
    {
      sectionTitle: "Legal & Documentation",
      fields: [
        { key: "titleDeed", type: "file", label: "Title Deed", tooltip: "Upload a scanned, clear copy of the property's Title Deed." },
        { key: "leaseAgreements", type: "file", label: "Lease Agreements", tooltip: "Upload a complete, signed copy of the current lease agreement(s)." },
        { key: "approvalsInPlace", type: "multiselect", label: "Approvals in Place", options: approvalsInPlaceIndustrialOptions, tooltip: "Select all applicable approvals: Industrial Zone Permit, Fire NOC, Building Completion Certificate." },
      ],
    },
    {
      sectionTitle: "Media",
      fields: [
        { key: "propertyDescription", type: "richtext", label: "Property Description", tooltip: "Focus on logistical advantages: highway connectivity, port/airport proximity, tenant quality, and building specifications." },
        { key: "propertyImages", type: "file", label: "Property Images", tooltip: "Upload photos of the exterior, loading docks, interior (showing height), and truck access areas." },
        { key: "floorPlanSiteLayout", type: "file", label: "Floor Plan / Site Layout", tooltip: "Upload a clear image or PDF of the site layout, including truck turning radius if possible." },
      ],
    },
  ],
  "Warehouse (Vacant)": [
    {
      sectionTitle: "Plot & Building Specifications",
      fields: [
        {
          key: "plotAreaUnit",
          type: "dropdown",
          label: "Plot Area Unit",
          options: buildingPlotAreaUnitOptions,
          placeholder: "Unit",
          tooltip: "Default Sq. Ft. Area is converted to sq. ft. for reports (plotAreaSqFt).",
        },
        { key: "plotArea", type: "number", label: "Plot Area", placeholder: "E.g. 5000" },
        { key: "superAreaSqFt", type: "number", label: "Super Area (sq. ft.)", placeholder: "" },
        { key: "ceilingHeight", type: "text", label: "Ceiling Height", placeholder: "" },
        { key: "structureType", type: "text", label: "Structure type", placeholder: "" },
        { key: "numberOfLoadingDocks", type: "number", label: "Number of Loading Docks", placeholder: "" },
        { key: "floorLoadCapacityKgSqM", type: "number", label: "Floor Load Capacity (kg/sq. m.)", placeholder: "" },
        { key: "powerLoadKva", type: "number", label: "Power Load (KVA)", placeholder: "" },
        { key: "truckAccessibility", type: "text", label: "Truck Accessibility", placeholder: "" },
        { key: "isCarParkingIncluded", type: "yesno", label: "Is Car Parking Included?", options: YES_NO },
        { key: "totalNumberCarParks", type: "number", label: "Total Number of Car Parks", placeholder: "", showWhen: { key: "isCarParkingIncluded", value: "Yes" } },
      ],
    },
    {
      sectionTitle: "Property Status",
      fields: [
        { key: "occupancyStatus", type: "dropdown", label: "Occupancy Status", options: STATUS_VACANT_PRE_RENTED, placeholder: "Vacant", tooltip: "Defaulted to Vacant for this listing." },
        { key: "constructionStatus", type: "dropdown", label: "Construction Status", options: constructionStatusOptions, placeholder: "Select status", tooltip: "Select the current status: Ready to Move, Under Construction." },
        { key: "ageOfProperty", type: "number", label: "Age of Property (years)", placeholder: "e.g. 5", tooltip: "How many years old is the building from its completion date?", showWhen: { key: "constructionStatus", value: "Ready to Move" } },
        { key: "possessionDate", type: "text", label: "Possession date", placeholder: "MMM/YYYY e.g. Mar/2025", tooltip: "Enter the expected month and year of possession.", showWhen: { key: "constructionStatus", value: "Under Construction" } },
        { key: "furnishingStatus", type: "dropdown", label: "Furnishing Status", options: furnishingOptions, placeholder: "Select", tooltip: "Select from: Unfurnished, Semi-Furnished, Fully-Furnished." },
        { key: "furnishingDetails", type: "textarea", label: "Furnishing Details", placeholder: "e.g. Includes AC, basic lighting", tooltip: "Briefly describe the included furnishings.", showWhen: { key: "furnishingStatus", value: "Semi-Furnished" } },
        { key: "furnishingDetails", type: "textarea", label: "Furnishing Details", placeholder: "e.g. Includes AC, basic lighting", tooltip: "Briefly describe the included furnishings.", showWhen: { key: "furnishingStatus", value: "Fully-Furnished" } },
      ],
    },
    {
      sectionTitle: "Financial Details",
      fields: [
        { key: "totalSalePrice", type: "number", label: "Total Price", placeholder: "e.g. 5000000", tooltip: "Enter the total expected sale price for the property. This is your listing price." },
        { key: "pricePerSqFt", type: "number", label: "Price Per Sq. Ft. [Auto-Calculated]", placeholder: "e.g. 12000", tooltip: "System will calculate this. Formula: (Total Price / Total Built-up Area)." },
        { key: "propertyTaxMunicipalTax", type: "number", label: "Property Tax / Municipal Tax", placeholder: "e.g. 50000", tooltip: "Mention the annual property tax paid to the local municipal authority." },
        { key: "estimatedMonthlyMaintenance", type: "number", label: "Estimated Monthly Maintenance", placeholder: "e.g. 15000", tooltip: "Enter the current monthly maintenance fee paid to the society or building management." },
      ],
    },
    {
      sectionTitle: "Legal & Documentation",
      fields: [
        { key: "titleDeed", type: "file", label: "Title Deed", tooltip: "Upload a scanned, clear copy of the property's Title Deed." },
        { key: "propertyTaxReceipts", type: "file", label: "Property Tax Receipts", tooltip: "Upload the most recent property tax payment receipt." },
        { key: "occupancyCertificateOc", type: "file", label: "Occupancy Certificate (OC)", tooltip: "If available, upload the Occupancy Certificate for the building." },
        { key: "approvalsInPlace", type: "multiselect", label: "Approvals in Place", options: approvalsInPlaceIndustrialOptions, tooltip: "Select all applicable approvals: Industrial Zone Permit, Fire NOC, Building Completion Certificate." },
      ],
    },
    {
      sectionTitle: "Media",
      fields: [
        { key: "propertyDescription", type: "richtext", label: "Property Description", tooltip: "Focus on logistical advantages: highway connectivity, port/airport proximity, tenant quality, and building specifications." },
        { key: "propertyImages", type: "file", label: "Property Images", tooltip: "Upload photos of the exterior, loading docks, interior (showing height), and truck access areas." },
        { key: "floorPlanSiteLayout", type: "file", label: "Floor Plan / Site Layout", tooltip: "Upload a clear image or PDF of the site layout, including truck turning radius if possible." },
        { key: "videoTourLink", type: "text", label: "Video Tour Link", placeholder: "https://...", tooltip: "Provide a link to a video walkthrough of the property." },
      ],
    },
  ],
}
